import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Intent, FileInput, Button, RadioGroup, Radio, Icon } from "@blueprintjs/core";
import { isEmpty, isEqual, merge } from "lodash";
import { saveAs } from "file-saver";
import { IInputProps } from "../types";
import { IFile, IUpload } from "components/Template/types";
import { doDisabled, doHidden } from "components/Template/utils";
import Validate from "components/Template/Validate";
import Override from "components/Template/Override";
import ChangeHighlight from "components/Template/ChangeHighlight";
import "./File.scss";

export interface IFileProps extends IInputProps {
  template: IFile;
}

export const checkValue = (template: IFile, v?: IUpload | null) => {
  if (v && template.mimetypes && !template.mimetypes?.includes(v?.mimetype)) {
    return `Upload file type must be one of: ${template.mimetypes.join(", ")}.`;
  } else if (
    v &&
    !template.mimetypes &&
    !(
      [v?.mimetype].includes(template.value.mimetype) ||
      [v?.filename.split(".").pop()].includes(template.value.filename.split(".").pop())
    )
  ) {
    return `Upload file type must be type: ${template.value.mimetype}`;
  }
  return "";
};

const readContent = (v?: File | null) => {
  return new Promise((resolve, reject) => {
    if (!v) {
      return resolve("");
    }
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(v);
  });
};

const OPTIONS = ["Use Default", "Upload My Own (*.csv)"];

const sanitizeDomName = (value: string) => value.replace(/[^A-Za-z0-9_-]/g, "_");

const File = (props: IFileProps) => {
  const { template, mutators, metadata } = props;
  const { getValue, handleChange } = mutators;
  const defaultPath = template.value.path;
  const fieldPath = mutators.getPath(template.name);
  const radioGroupName = sanitizeDomName(`${fieldPath}-radio`);
  const fileInputId = sanitizeDomName(`${fieldPath}-file`);

  const [value, setValue] = useState(getValue(template.name) as IUpload);
  const [fault, setFault] = useState("");

  const v = getValue(template.name) as IUpload;
  const modelValue = isEqual(v, value) ? value : v;

  const [valueRadio, setValueRadio] = useState(isEmpty(value.value) ? OPTIONS[0] : OPTIONS[1]);
  const previousValueRadio = useRef(valueRadio);

  const transformInput = useCallback(
    (v?: File | null) => {
      const tempf = v ? ({ mimetype: v.type, filename: v.name } as IUpload) : null;
      const f = checkValue(template, tempf);
      readContent(v)
        .then((c) => {
          const u: IUpload = v
            ? {
              mimetype: v.type,
              filename: v.name.toString(),
              value: c as string,
              path: defaultPath,
            }
            : template.value;
          setValue(u);
          setFault(f);
          handleChange && handleChange(template.name, u);
        })
        .catch((e) => setFault(e));
    },
    [template, handleChange, defaultPath]
  );

  useEffect(() => {
    const switchedToDefault = valueRadio === OPTIONS[0] && previousValueRadio.current !== OPTIONS[0];
    previousValueRadio.current = valueRadio;
    if (switchedToDefault) {
      transformInput();
    }
  }, [valueRadio, transformInput]);
  const download = useCallback(() => {
    const file = merge(
      new Blob([modelValue.value ? modelValue.value : ""], {
        type: modelValue.mimetype,
      }),
      {
        lastModifiedDate: new Date(),
        name: modelValue.filename,
      }
    );
    saveAs(file, file.name);
  }, [modelValue]);

  if (doHidden(props)) {
    if (!metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput.push(template.name);
    }
    return null;
  } else {
    if (metadata.hiddenInput.includes(template.name)) {
      metadata.hiddenInput = metadata.hiddenInput.filter((i) => i === template.name);
    }
  }
  const disabled = doDisabled(props);
  const faulted = !isEmpty(fault);
  const changePath = mutators.getPath(template.name);

  return (
    <Validate
      {...props}
      className="file"
      intent={faulted ? Intent.DANGER : Intent.NONE}
      helperText={
        faulted ? (
          <span className="fault">
            <Icon icon="error" intent={Intent.DANGER} /> {fault}
          </span>
        ) : (
          ""
        )
      }
      label={template.label}
      showLabel
      showInfo
    >
      <ChangeHighlight changed={false} hideActions changePath={changePath}>
        <div className="file-override">
          <RadioGroup
            name={radioGroupName}
            selectedValue={valueRadio}
            {...(handleChange
              ? {
                onChange: (e) => setValueRadio(e.currentTarget.value),
                disabled: disabled,
              }
              : {
                onChange: () => { },
                readOnly: true,
              })}
          >
            {OPTIONS.map((option) => (
              <Radio key={option} label={option} value={option}></Radio>
            ))}
          </RadioGroup>
          <Override {...props} />
        </div>
        {valueRadio !== OPTIONS[0] && (
          <Fragment>
            <FileInput
              key={`${template.name}-${value.filename}`}
              className="file__input"
              id={fileInputId}
              text={modelValue ? modelValue.filename : "Choose file..."}
              buttonText="Upload"
              {...(handleChange
                ? {
                  onInputChange: (e) => transformInput(e.currentTarget?.files?.item(0)),
                }
                : { readOnly: true })}
              disabled={handleChange ? disabled : true}
            />
          </Fragment>
        )}
        <div className="file__buttons">
          {valueRadio !== OPTIONS[0] && (
            <Fragment>
              <Button
                minimal
                icon="delete"
                text="Delete Current File"
                intent="primary"
                disabled={modelValue.value ? false : true}
                onClick={() => transformInput()}
              />
              <Button
                minimal
                icon="download"
                text="Download Current File"
                intent="primary"
                disabled={modelValue.value ? false : true}
                onClick={download}
              />
            </Fragment>
          )}
          {defaultPath && (
            <a href={defaultPath} target="_blank" rel="noreferrer">
              <Button icon="download" minimal intent="primary" text={valueRadio === OPTIONS[0] ? "Download Default File" : "Download Template"} disabled={disabled} />
            </a>
          )}
        </div>
      </ChangeHighlight>
    </Validate>
  );
};

export default File;
